import { useState, useEffect, useMemo, useRef } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {
  TextField,
  InputAdornment,
  Typography,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import Menu from "@mui/material/Menu";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreateIcon from "@mui/icons-material/Create";
import Search from "@mui/icons-material/Search";
import AudienceFormDialog from "./AudienceFormDialog";
import AudienceFileFormDialog from "./AudienceFileFormDialog";
import {
  AUDIENCE_GET_ENDPOINT,
  FILE_UPLOAD_AUDIENCE_ENDPOINT,
  AUDIENCE_EXPORT_ENDPOINT,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import {
  Add,
  Close,
  CopyAll,
  Download,
  PersonAdd,
  Sell,
  Settings,
} from "@mui/icons-material";
import TableSearchInput from "../TableSearchInput";
import AddAudienceModal from "./AddAudienceModal";
import EditAudienceModal from "./EditAudienceModal";
import CreateTagModal from "./Tags/CreateTagModal";
import BulkUpdateModal from "./BulkUpdateModal";
import Filter from "../Filter";
import AudienceTableComponent from "./AudienceTableComponent";
import ManageTagsModal from "./Tags/ManageTagsModal";
import {
  borderColors,
  ColorsConstants,
  grayFontColor,
} from "../../cssUtils/constants";
import ButtonTwo from "../../common/dashboard-button";
import ButtonOne from "../../common/normal-button";
import typography from "../../cssUtils/typography";
import AppDropdown from "../../common/app-dropdown";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import {
  resolveSearchQueryForApi,
  normalizeCombinedSearchInput,
  PHONE_SEARCH_ERROR_MESSAGE,
} from "../../utils/phoneSearch";

const LARGE_UPLOAD_TIMEOUT_IN_SECONDS = 45;

// The dialog previews this many skipped rows; the downloaded file has all of them.
const SKIPPED_ROWS_PREVIEW_LIMIT = 50;

const SKIPPED_ROWS_COLUMNS = [
  { id: "row", label: "Row", minWidth: 60 },
  { id: "phone_no", label: "Phone Number", minWidth: 130 },
  { id: "reason", label: "Reason", minWidth: 240 },
];

// Prefixes cells that a spreadsheet would otherwise run as a formula.
const toSafeCsvCell = (value) => {
  const text = String(value ?? "");
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

// const permissionsFromStorage = JSON.parse(
//   localStorage.getItem("permissions") || "[]",
// );
// const hasDeletePermission = permissionsFromStorage.includes("audience.delete");
// const hasEditPermission = permissionsFromStorage.includes("audience.edit");
const DEFAULT_COLUMNS = [
  { id: "select", label: "Select" },
  { id: "company", label: "Company" },
  { id: "name", label: "Contact Name" },
  { id: "phone_no", label: "Phone" },
  { id: "dob", label: "Date of Birth" },
  { id: "city", label: "City" },
  { id: "state", label: "State" },
  { id: "tags", label: "Tags" },
  { id: "opt_in", label: "Opted In" },
  { id: "updated_by", label: "Updated By" },
  { id: "last_updated", label: "Last Updated" },
  // Only include the actions column if user has delete permission
  { id: "", label: "" },
];

const tenantSpecificColumns = {
  "oneTouchIndiaMarketingLive" : [
    {id: 'state', label: "State"}
  ]
}

export default function AudienceTable({
  refreshKey,
  updateRefreshKey,
  onNavigate,
}) {
  const { getValidToken } = useAuth();

  const hasDeletePermission = useMemo(() => {
    const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
    return permissions.includes("audience.delete");
  }, []); 

  const hasEditPermission = useMemo(() => {
    const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
    return permissions.includes("audience.edit");
  }, []); 

  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [rowCount, setRowCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [audienceData, setAudienceData] = useState([]);
  const [audienceModalState, setAudienceModalState] = useState({
    isOpen: false,
    selectedClient: null,
    mode: "add",
    jsonData: "",
  });
  const [audienceFileModalState, setAudienceFileModalState] = useState({
    isOpen: false,
    mode: "add",
    jsonData: "",
  });
  const URL =
    process.env.NODE_ENV === "production" ? "api" : `http://localhost:8000/api`;
  const [controller, setController] = useState({
    page: 0,
    serverSidePage: 0,
    rowsPerPage: 20,
  });
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [allTagOptions, setAllTagOptions] = useState([]);

  const tenant = localStorage.getItem("tenant");

  const sampleFileURL = "/sample.csv"; // File should be inside `public/sample.pdf`

  const audienceAddPermission = "audience.create";
  const permissions = localStorage.getItem("permissions") || [];

  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);

  const [addAudienceModalOpen, setAddAudienceModalOpen] = useState(false);
  const [editAudienceModalOpen, setEditAudienceModalOpen] = useState(false);
  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);
  const [manageTagsModalOpen, setManageTagsModalOpen] = useState(false);
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);

  const [selectedEditAudience, setSelectedEditAudience] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [exportState, setExportState] = useState("default");
  const [exportSelectedState, setExportSelectedState] = useState("default");
  const [exportError, setExportError] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(false);

  const [activeFilters, setActiveFilters] = useState([]);

  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [largeFileTimeout, setLargeFileTimeout] = useState(false);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const largeFileTimeoutRef = useRef(false);
  const [audienceLoading, setAudienceLoading] = useState(false);

  const [selectedRecords, setSelectedRecords] = useState([]);

  const [filterLoading, setFilterLoading] = useState(false);

  const [hasSelectedAll, setHasSelectedAll] = useState(false);

  const hasAddPermission = permissions.includes(audienceAddPermission);

const filterFields = [
  { label: "Name", type: "text" },
  { label: "Phone", type: "phone" },
  { label: "Email", type: "text" },
  { label: "Language", type: "text" }, // ya list
  // Multi-select, but only the operators the backend's encrypted gender filter supports
  { label: "Gender", type: "list", operators: ["is", "is not", "is empty", "is not empty"], values: ["Male", "Female", "Other"] },
  // Birthday-month filter: sends the month name, the backend matches on month only.
  // Label becomes the "dob" query key the backend expects.
  {
    label: "DOB",
    type: "single_select_list",
    values: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ],
  },
  { label: "City", type: "text" },
  { label: "State", type: "text" },
  {
    label: "Tags",
    type: "list",
    values: allTagOptions.map((tag) => tag.name),
  },
  { label: "Marketing Opt In", type: "boolean", display: ["Yes", "No"] },
  { label: "Updated By", type: "text" },
  { label: "Last Updated", type: "date" },
];

  // Handlers
  const handleActionsClick = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const handleExportFromMenu = async () => {
    if (exportState === "default") {
      setExportState("loading");
      handleActionsClose();
      try {
        await handleExport("filter"); // Wait for export to complete
        setExportState("success");
      } catch (error) {
        setExportState("failed");
      }
    }
  };

  const handleAddFromMenu = () => {
    setAddAudienceModalOpen(true);
    handleActionsClose();
  };

  const handleManageTagsFromMenu = () => {
    setManageTagsModalOpen(true);
    handleActionsClose();
  };

  const handleUploadFromMenu = () => {
    handleOpen();
    handleActionsClose();
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0]; // Only allow a single file
    if (selectedFile) {
      // A new selection starts a new upload: drop the previous upload's skipped-rows summary
      setUploadResult(null);
      // check if file is .csv
      if (!selectedFile.name.endsWith(".csv")) {
        setError({ summary: "Please upload a CSV file", details: [] });
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null); // clear error on valid file
    }

    // Reset input so selecting the same file again will still trigger onChange
    event.target.value = "";
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setError(null);
    setUploadSuccess(false);
    setUploadResult(null);
    setLargeFileTimeout(false);
  };

  const handleDownloadSkippedRows = () => {
    if (!uploadResult?.rows?.length) return;
    const lines = [
      "Row,Phone Number,Reason",
      ...uploadResult.rows.map((r) =>
        [r.row, r.phone_no, r.reason].map(toSafeCsvCell).join(","),
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "skipped-rows.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const updateSearchValue = (value) => {
    const normalized = normalizeCombinedSearchInput(value);
    if (normalized.length > 50) {
      return;
    }
    setSearchQuery(normalized);
    setCharCount(normalized.length);
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all records that are currently displayed (respecting filters)
      setHasSelectedAll(true);
    } else {
      // Deselect all records
      setHasSelectedAll(false);
    }
  };

  const handleSelectRecord = (event, audience) => {
    const id = audience.id;
    const selectedIndex = selectedRecords.indexOf(id);
    let newSelected = [];
    let newBulkUpdateData = [];

    if (selectedIndex === -1) {
      // Add to selection
      newSelected = [...selectedRecords, id];
      newBulkUpdateData = [
        ...bulkUpdateData,
        {
          id: id,
          tags: audience.tags,
        },
      ];
    } else {
      // Remove from selection
      newSelected = selectedRecords.filter((recordId) => recordId !== id);
      newBulkUpdateData = bulkUpdateData.filter((record) => record.id !== id);
    }

    setSelectedRecords(newSelected);
    setBulkUpdateData(newBulkUpdateData);
  };

  const handleClearSelection = () => {
    setSelectedRecords([]);
    setBulkUpdateData([]);
  };

  const handleSelectedAllClear = () => {
    setHasSelectedAll(false);
  };

  const isRecordSelected = (id) => selectedRecords.indexOf(id) !== -1;

  useEffect(() => {
    if (audienceData.length === 0) {
      setHasSelectedAll(false);
      setSelectedRecords([]);
      setBulkUpdateData([]);
    }
  }, [audienceData]);

  useEffect(() => {
    if (!exportError) return;
    const timer = setTimeout(() => setExportError(null), 3000);
    return () => clearTimeout(timer);
  }, [exportError]);

  const handleUpload = async () => {
    if (!file) return;

    if (file.type !== "text/csv" && file.type !== "application/vnd.ms-excel") {
      setError({
        summary: "Invalid file type. Please upload a CSV file.",
        details: [],
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError({
        summary: "File size exceeds 10MB. Please upload a smaller file.",
        details: [],
      });
      return;
    }

    setLoading(true);
    setError(null);
    setUploadSuccess(false);
    setUploadResult(null);
    setLargeFileTimeout(false);
    largeFileTimeoutRef.current = false;

    // Timeout for long upload
    const uploadTimeout = setTimeout(() => {
      largeFileTimeoutRef.current = true;
      setError({
        summary:
          "Your file is being processed in the background due to its size. We’ll notify you by email once processing is complete.",
        details: [],
      });
      setLargeFileTimeout(true);
      setLoading(false);
    }, LARGE_UPLOAD_TIMEOUT_IN_SECONDS * 1000);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const tenant = localStorage.getItem("tenant");
      const user = JSON.parse(localStorage.getItem("user")); // Parse JSON string into an object
      if (user && user.id) {
        formData.append("user", user.id);
      } else {
        console.error("User ID not found in localStorage.");
      }

      const response = await fetch(FILE_UPLOAD_AUDIENCE_ENDPOINT, {
        method: "POST",
        body: formData,
        headers: {
          "X-Tenant-ID": tenant,
        },
      });

      // Clear the timeout if the upload completes before the timeout
      clearTimeout(uploadTimeout);

      const data = await response.json(); // Convert response to JSON

      if (!response.ok) {
        if (data.errors && data.errors.file) {
          const errorSummary =
            data.errors.file.error_summary ||
            "File upload failed due to validation errors.";
          const errorDetails = data.errors.file.errors || [];
          throw {
            summary: errorSummary,
            details: errorDetails,
            rows: data.errors.file.rejected_rows || [],
            processed: data.errors.file.processed_count,
            total: data.errors.file.total_rows,
          };
        } else {
          throw {
            summary: "File upload failed. Please try again.",
            details: [],
          };
        }
      }

      // Reset state on success
      fetchData();

      setFile(null);
      setError(null);

      // Some rows were skipped: keep the dialog open so the user can read the details
      if (data.rejected_count > 0) {
        setUploadResult({
          processed: data.processed_count ?? 0,
          rejected: data.rejected_count,
          total: (data.processed_count ?? 0) + data.rejected_count,
          rows: data.rejected_rows || [],
        });
        setUploadSuccess(false);
        return;
      }

      setUploadSuccess(true);
      setTimeout(() => {
        setUploadSuccess(false);
        handleClose();
      }, 3000);
    } catch (err) {
      console.error("Error uploading file:", err);
      clearTimeout(uploadTimeout);
      if (largeFileTimeoutRef.current) {
        // The "processed in the background" notice has already been shown and
        // the backend is processing (and will email) independently of this
        // request's outcome, so don't overwrite it with a generic error.
        return;
      }
      setUploadSuccess(false);
      // Row-level validation failures use the same summary + table as partial success
      if (err.rows?.length > 0) {
        const processed = Number(err.processed) || 0;
        setError(null);
        setUploadResult({
          processed,
          rejected: err.rows.length,
          total: Number(err.total) || processed + err.rows.length,
          rows: err.rows,
        });
        return;
      }
      setError({
        summary: err.summary || "An error occurred.",
        details: err.details || [],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (mode = "filter") => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      let url = AUDIENCE_EXPORT_ENDPOINT;

      if (mode === "ids" && selectedRecords && selectedRecords.length > 0) {
        // Export by IDs (override filters)
        const ids = selectedRecords.join(",");
        url += `?ids=${ids}`;
      } else {
        const exportSearch = resolveSearchQueryForApi(searchQuery);
        if (exportSearch.blockFetch) {
          setExportError(PHONE_SEARCH_ERROR_MESSAGE);
          return;
        }
        // Export by Filters
        let queryParams = new URLSearchParams({
          limit: controller.rowsPerPage.toString(),
          offset: (
            controller.serverSidePage * controller.rowsPerPage
          ).toString(),
          search_query: exportSearch.apiSearchQuery,
        });

        // apply active filters dynamically
        activeFilters.forEach(({ field, operator, value }) => {
          field = field.toLowerCase().replaceAll(" ", "_");
          operator = operator.toLowerCase().replaceAll(" ", "_");
          queryParams.append(`${field}`, `${operator}:${value ?? ""}`);
        });

        url += `?${queryParams}`;
      }

      // TIMEOUT HANDLING: Set timeout for 1 minute (60000 ms)
      const TIMEOUT_DURATION = 60000; // 1 minute
      let timeoutMessage = null; // Variable for timeout message as requested

      console.log("Export URL:", url);

      // Create AbortController for actual request cancellation
      const apiController = new AbortController();
      const timeoutId = setTimeout(
        () => apiController.abort(),
        TIMEOUT_DURATION,
      );

      console.log("Starting export request with timeout...");

      // Make API request to export endpoint
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
        signal: apiController.signal, // Add abort signal
      });

      // Clear timeout if request completes in time
      clearTimeout(timeoutId);

      if (!response.ok) {
        try {
          const error = (await response.json()).error;
          setExportError(error);
          throw new Error(error);
        } catch {
          throw new Error("Failed to export data");
        }
      }

      // FIX: Check content type before parsing JSON
      const contentType = response.headers.get("content-type");
      let responseData;

      if (contentType && contentType.includes("application/json")) {
        // Handle JSON response (async processing notification)
        responseData = await response.json();
        if (responseData.status === "processing") {
          // Set timeout message variable as requested
          timeoutMessage = responseData.message;
          // You can use this variable to display the message in UI later
          console.log("Async processing started:", timeoutMessage);

          // Show alert to user about async processing
          // alert(responseData.message);
          setExportError(timeoutMessage);
          return; // Exit early since file will come via email
        }
      } else {
        // Handle CSV response - clone response for blob processing
        const responseClone = response.clone();
        const blob = await responseClone.blob();

        // Create a link element to download the file
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = urlBlob;

        // Use the current date for the filename
        const today = new Date();
        const dateStr = today.toISOString().split("T")[0];
        a.download = `audience-data-${dateStr}.csv`;

        // Append to the document and trigger download
        document.body.appendChild(a);
        a.click();

        // Clean up
        window.URL.revokeObjectURL(urlBlob);
        document.body.removeChild(a);
        return; // Exit after successful CSV download
      }
    } catch (error) {
      // Handle timeout specifically
      if (error.name === "AbortError") {
        console.error("Export request timed out");
        // Set timeout message variable
        let timeoutMessage =
          "The export is being processed due to large data volume. You'll receive the CSV file via email shortly.";
        // Show user message
        // alert(timeoutMessage);
        setExportError(timeoutMessage);
        return; // Exit gracefully
      }

      console.error("Error exporting data:", error.message);
      throw error;
    }
  };

  useEffect(() => {
    if (
      exportState &&
      (exportState === "success" || exportState === "failed")
    ) {
      setTimeout(() => {
        setExportState("default");
      }, 3000);
    }
  }, [exportState]);

  const resolvedListSearch = useMemo(
    () => resolveSearchQueryForApi(searchQuery),
    [searchQuery],
  );

  const fetchData = async () => {
    if (resolvedListSearch.blockFetch) {
      setAudienceData([]);
      setRowCount(0);
      if (filterLoading) {
        setFilterLoading(false);
      }
      setAudienceLoading(false);
      return;
    }
    setAudienceLoading(true);
    let queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.serverSidePage * controller.rowsPerPage).toString(),
      search_query: resolvedListSearch.apiSearchQuery,
    });

    // apply active filters dynamically
    activeFilters.forEach(({ field, operator, value }) => {
      field = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      queryParams.append(`${field}`, `${operator}:${value ?? ""}`);
    });

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(`${AUDIENCE_GET_ENDPOINT}?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();

      setAudienceData(jsonData.results);
      setRowCount(jsonData.count);
      setAllTagOptions(jsonData.tag_options || []);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    } finally {
      if (filterLoading) {
        setFilterLoading(false);
      }
      setAudienceLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    controller.serverSidePage,
    controller.rowsPerPage,
    refreshKey,
    searchQuery,
    activeFilters,
  ]);

  // pagination
  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
      serverSidePage: newPage, // Sync server-side page with UI page
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const value = Number(event.target.value);

    setController((prev) => ({
      ...prev,
      rowsPerPage: value,
      page: 0,
      serverSidePage: 0,
    }));
  };

  const handleDeleteAudienceClick = (audience) => {
    setAudienceModalState({
      isOpen: true,
      selectedClient: audience,
      mode: "delete_audience",
    });
  };

  const handleSelectedAudienceDeleteClick = () => {
    setAudienceModalState({
      isOpen: true,
      selectedClients: selectedRecords,
      mode: "delete_selected_audience",
    });
  };

  const handleSelectedAllDeleteClick = () => {
    setAudienceModalState({
      isOpen: true,
      activeFilters: activeFilters,
      count: rowCount,
      mode: "delete_all_audience",
    });
  };

  // Add Tenant Specific Columns to Columns
  useEffect(() => {
    const tenantColumns = tenantSpecificColumns[tenant];

    if (tenantColumns && tenantColumns.length > 0) {
      tenantColumns.forEach((column) => {
        setColumns((prevColumns) => {
          if (!prevColumns.find((col) => col.id === column.id)) {
            return [
              ...prevColumns.slice(0, -2),
              column,
              ...prevColumns.slice(-2),
            ];
          } else {
            return prevColumns;
          }
        });
      });
    }
  }, [tenant]);

  const handleFiltersChange = (filters) => {
    setFilterLoading(true);
    setActiveFilters(filters);
    setController({
      page: 0,
      serverSidePage: 0,
      rowsPerPage: 20,
    });

    if (filters.length === 0) {
      setHasSelectedAll(false);
    }
  };

  return (
    <div>
      {isMobile ? (
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            bgcolor: "#FAFAFA",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Menu
              anchorEl={actionsAnchorEl}
              open={Boolean(actionsAnchorEl) && exportState !== "loading"}
              onClose={handleActionsClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                elevation: 0,
                sx: {
                  minWidth: "180px",
                  border: "1px solid #DCDFE5",
                  borderRadius: "8px",
                  mt: 0.5,
                  "& .MuiMenuItem-root": {
                    ...typography.bodySmall,
                    py: 1,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "#F0F7FF",
                    },
                  },
                },
              }}
            >
              <MenuItem onClick={handleExportFromMenu}>
                <DownloadIcon sx={{ mr: 1, fontSize: 18, color: "#6C7C93" }} />
                <Typography sx={typography.bodySmall}>
                  Export all data
                </Typography>
              </MenuItem>

              {hasAddPermission && (
                <MenuItem onClick={handleAddFromMenu}>
                  <PersonAdd sx={{ mr: 1, fontSize: 18, color: "#6C7C93" }} />
                  <Typography sx={typography.bodySmall}>
                    Add Audience
                  </Typography>
                </MenuItem>
              )}

              {hasAddPermission && (
                <MenuItem onClick={handleManageTagsFromMenu}>
                  <Sell sx={{ mr: 1, fontSize: 18, color: "#6C7C93" }} />
                  <Typography sx={typography.bodySmall}>Manage Tags</Typography>
                </MenuItem>
              )}

              {hasAddPermission && (
                <MenuItem onClick={handleUploadFromMenu}>
                  <CloudUploadIcon
                    sx={{ mr: 1, fontSize: 18, color: "#6C7C93" }}
                  />
                  <Typography sx={typography.bodySmall}>
                    Upload Audience
                  </Typography>
                </MenuItem>
              )}
            </Menu>
          </Box>
          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              width: "100%",
            }}
          >
            <TableSearchInput
              searchQuery={searchQuery}
              onSearchChange={(e) => updateSearchValue(e.target.value)}
              charCount={charCount}
              tooltip="Search by Name, Phone Number, Email"
              placeholder="Search by Name, Phone Number, Email"
              error={Boolean(resolvedListSearch.blockFetch && resolvedListSearch.phoneSearchError)}
              helperText={resolvedListSearch.blockFetch ? resolvedListSearch.phoneSearchError : undefined}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
              mt: 1,
              width: "100%",
            }}
          >
            <Filter
              fields={filterFields}
              onApply={(filters) => handleFiltersChange(filters)}
              loading={filterLoading}
            />
            <Button
              variant="outlined"
              onClick={handleActionsClick}
              disabled={exportState === "loading"}
              startIcon={
                exportState === "loading" ? (
                  <CircularProgress size={20} />
                ) : undefined
              }
              endIcon={
                exportState === "loading" ? undefined : (
                  <Box
                    component="span"
                    sx={{ ...typography.bodySmall, ml: 0.5 }}
                  >
                    ▼
                  </Box>
                )
              }
              sx={{
                height: "40px",
                textTransform: "none",
                color: "#6C7C93",
                borderColor: "#DCDFE5",
                borderRadius: "8px",
                flex: "0 0 auto",
                ...typography.bodyLarge,
                "&:hover": {
                  borderColor: ColorsConstants.primaryBackgroundColor,
                  color: ColorsConstants.primaryBackgroundColor,
                  borderWidth: "1px",
                  backgroundColor: "transparent",
                },
                "&:active": {
                  borderColor: ColorsConstants.primaryBackgroundColor,
                  color: ColorsConstants.primaryBackgroundColor,
                  borderWidth: "1px",
                },
              }}
            >
              {exportState === "loading" ? "Exporting..." : "Actions"}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            mb: 2,
            display: { xs: "none", sm: "flex" },
            width: "100%",
            gap: 1,
            alignItems: "center",
            bgcolor: "#FAFAFA",
          }}
        >
          {/* Selected Records Display - Full width when active */}
          {audienceData.length > 0 &&
            ((selectedRecords.length > 0) || (hasSelectedAll && rowCount > 0)) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                gap: 1,
                p: 1,
                border: `1px solid ${ColorsConstants.primaryBackgroundColor}`,
                borderRadius: "8px",
                backgroundColor: "#F0F7FF",
              }}
            >
              <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
                {hasSelectedAll
                  ? `${rowCount} audiences on this page are selected`
                  : `${selectedRecords.length} audiences on this page are selected`}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <ButtonTwo
                  onClick={
                    hasSelectedAll
                      ? handleSelectedAllClear
                      : handleClearSelection
                  }
                  sx={{
                    textTransform: "none",
                    height: "36px",
                    ...typography.bodySmall,
                  }}
                  title={"Clear Selection"}
                >
                  Clear Selection
                </ButtonTwo>

                <Button
                  onClick={
                    hasSelectedAll
                      ? handleSelectedAllDeleteClick
                      : handleSelectedAudienceDeleteClick
                  }
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #DCDFE5",
                    color: "#E74C3C",
                    textTransform: "none",
                    height: "36px",
                    ...typography.bodySmall,
                    "&:hover": {
                      borderColor: "#E74C3C",
                      backgroundColor: "rgba(231, 76, 60, 0.04)",
                    },
                  }}
                >
                  <DeleteIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Delete
                </Button>

                <Button
                  onClick={async () => {
                    setExportSelectedState("loading");
                    await handleExport(hasSelectedAll ? "filter" : "ids");
                    setExportSelectedState("default");
                  }}
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #DCDFE5",
                    textTransform: "none",
                    height: "36px",
                    ...typography.bodySmall,
                    "&:hover": {
                      borderColor: ColorsConstants.primaryBackgroundColor,
                    },
                  }}
                >
                  {exportSelectedState === "loading" ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 0.5 }} />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download
                        sx={{
                          mr: 0.5,
                          fontSize: 16,
                          color: ColorsConstants.primaryBackgroundColor,
                        }}
                      />
                      Export Selected
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => setBulkUpdateModalOpen(true)}
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #DCDFE5",
                    textTransform: "none",
                    height: "36px",
                    ...typography.bodySmall,
                    "&:hover": {
                      borderColor: ColorsConstants.primaryBackgroundColor,
                    },
                  }}
                >
                  <CopyAll sx={{ mr: 0.5, fontSize: 16 }} />
                  Bulk Update
                </Button>
              </Box>
            </Box>
          )}

          {!selectedRecords.length && !hasSelectedAll && (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <TableSearchInput
                  searchQuery={searchQuery}
                  onSearchChange={(e) => updateSearchValue(e.target.value)}
                  charCount={charCount}
                  tooltip="Search by Name, Phone Number, Email"
                  placeholder="Search by Name, Phone Number, Email"
                  error={Boolean(resolvedListSearch.blockFetch && resolvedListSearch.phoneSearchError)}
                  helperText={resolvedListSearch.blockFetch ? resolvedListSearch.phoneSearchError : undefined}
                />
              </Box>

              {/* Filter Button - Fixed width */}
              <Box sx={{ flex: "0 0 auto" }}>
                <Filter
                  fields={filterFields}
                  onApply={(filters) => handleFiltersChange(filters)}
                  loading={filterLoading}
                />
              </Box>

              <Box sx={{ flex: "0 0 auto" }}>
                <AppDropdown
                  disabled={exportState === "loading"}
                  startIcon={
                    exportState === "loading" ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                  endIcon={
                    exportState === "loading" ? undefined : (
                      <KeyboardArrowDownOutlinedIcon />
                    )
                  }
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  paperProps={{
                    elevation: 0,
                    style: {
                      minWidth: "180px",
                      border: "1px solid #DCDFE5",
                      borderRadius: "8px",
                      marginTop: "4px",
                    },
                  }}
                  menuItems={[
                    { label: "export", value: "export" },
                    ...(hasAddPermission
                      ? [
                          { label: "add", value: "add" },
                          { label: "manageTags", value: "manageTags" },
                          { label: "upload", value: "upload" },
                        ]
                      : []),
                  ]}
                  renderItem={(item, _isSelected, onSelect) => {
                    const iconSx = { mr: 1, fontSize: 18, color: "#6a7079" };
                    const map = {
                      export: {
                        icon: <DownloadIcon sx={iconSx} />,
                        label: "Export all data",
                        handler: handleExportFromMenu,
                      },
                      add: {
                        icon: <PersonAdd sx={iconSx} />,
                        label: "Add Audience",
                        handler: handleAddFromMenu,
                      },
                      manageTags: {
                        icon: <Sell sx={iconSx} />,
                        label: "Manage Tags",
                        handler: handleManageTagsFromMenu,
                      },
                      upload: {
                        icon: <CloudUploadIcon sx={iconSx} />,
                        label: "Upload Audience",
                        handler: handleUploadFromMenu,
                      },
                    };
                    const { icon, label, handler } = map[item.value];
                    return (
                      <MenuItem
                        key={item.value}
                        onClick={() => {
                          onSelect(item.value, item);
                          handler();
                        }}
                        sx={{
                          ...typography.bodySmall,
                          py: 1,
                          px: 2,
                          "&:hover": { backgroundColor: "#F0F7FF" },
                        }}
                      >
                        {icon}
                        <Typography sx={typography.bodySmall}>
                          {label}
                        </Typography>
                      </MenuItem>
                    );
                  }}
                  sx={{
                    height: "40px",
                    color: "#000000DE",
                    px: 2,
                    ...typography.bodyLarge,
                    "&:hover": {
                      borderColor: ColorsConstants.primaryBackgroundColor,
                      color: ColorsConstants.primaryBackgroundColor,
                      backgroundColor: "transparent",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#DCDFE5",
                      color: "#9AA6B8",
                    },
                  }}
                >
                  {exportState === "loading" ? "Exporting..." : "Actions"}
                </AppDropdown>
              </Box>
            </Box>
          )}
        </Box>
      )}

      <AudienceTableComponent
        hasDeletePermission={hasDeletePermission}
        hasEditPermission={hasEditPermission}
        columns={columns}
        audienceData={audienceData}
        rowCount={rowCount}
        controller={controller}
        tenant={tenant}
        tenantSpecificColumns={tenantSpecificColumns}
        hasSelectedAll={hasSelectedAll}
        handleSelectAll={handleSelectAll}
        handleSelectRecord={handleSelectRecord}
        isRecordSelected={isRecordSelected}
        setSelectedEditAudience={setSelectedEditAudience}
        setEditAudienceModalOpen={setEditAudienceModalOpen}
        handleDeleteAudienceClick={handleDeleteAudienceClick}
        handlePageChange={handlePageChange}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        audienceLoading={audienceLoading}
      />

      <AudienceFormDialog
        modalState={audienceModalState}
        handleModalState={setAudienceModalState}
        selectedClient={audienceModalState.selectedClient}
        fetchData={fetchData}
        selectedRecords={selectedRecords}
        setSelectedRecords={setSelectedRecords}
        audienceData={audienceData}
      />

      <AudienceFileFormDialog
        modalState={audienceFileModalState}
        handleModalState={setAudienceFileModalState}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={uploadResult ? "md" : "sm"}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Upload Audience
          <IconButton aria-label="close" onClick={handleClose}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            p={2}
            width="100%"
          >
            <Typography variant="body2" component="span">
              Click{" "}
              <Typography
                variant="body2"
                component="a"
                href={sampleFileURL}
                download="sample.csv"
                style={{
                  textDecoration: "none",
                  cursor: "pointer",
                  color: "blue",
                }}
              >
                here
              </Typography>{" "}
              to download sample file
            </Typography>
            <Typography
              variant="body2"
              component="span"
              style={{ textDecoration: "none", color: "grey" }}
            >
              Max. 10,000 rows per upload
            </Typography>
            {/* Hidden File Input */}
            <input
              type="file"
              accept="text/csv,application/vnd.ms-excel"
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="file-input"
            />
            <label htmlFor="file-input">
              <ButtonOne
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={loading}
              >
                Select File
              </ButtonOne>
            </label>

            {/* Display Selected File */}
            {file && (
              <Box textAlign="center">
                <Typography variant="subtitle1">Selected File:</Typography>
                <Typography variant="body2">{file.name}</Typography>
              </Box>
            )}

            {/* Error Alert */}
            {error && (
              <Alert
                severity={largeFileTimeout ? "info" : "error"}
                sx={{ width: "100%" }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {error.summary}
                </Typography>
                {error.details.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {(showAllErrors
                      ? error.details
                      : error.details.slice(0, 5)
                    ).map((errMsg, index) => (
                      <li key={index}>{errMsg}</li>
                    ))}
                  </ul>
                )}
                {error.details.length > 5 && (
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: "pointer",
                      color: "blue",
                      textDecoration: "underline",
                      mt: 1,
                    }}
                    onClick={() => setShowAllErrors(!showAllErrors)}
                  >
                    {showAllErrors ? "Show less..." : "Read more..."}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Partial Success: some rows were skipped */}
            {uploadResult && (
              <Box sx={{ width: "100%", minWidth: 0 }}>
                <Alert severity="warning" sx={{ width: "100%", mb: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {uploadResult.processed > 0
                      ? "Upload completed with skipped rows"
                      : "No rows were uploaded"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1 }}>
                    <AppChip
                      label={`Processed: ${uploadResult.processed}`}
                      color="success"
                    />
                    <AppChip
                      label={`Skipped: ${uploadResult.rejected}`}
                      color="warning"
                    />
                    <AppChip
                      label={`Total: ${uploadResult.total}`}
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2">
                    Skipped rows were not added. Fix them and upload again.
                  </Typography>
                </Alert>
                {/* The table scrolls on its own inside the dialog, so it must not
                    size itself to the page or lock the page scroll. */}
                <AppTable
                  columns={SKIPPED_ROWS_COLUMNS}
                  rows={uploadResult.rows.slice(0, SKIPPED_ROWS_PREVIEW_LIMIT)}
                  rowKey="row"
                  maxHeight={220}
                  autoAdjustHeight={false}
                  hideMainScroll={false}
                  paperSx={{ mb: 0 }}
                  headCellSx={{ padding: "8px 12px" }}
                  bodyCellSx={{
                    padding: "8px 12px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                />
                {uploadResult.rows.length > SKIPPED_ROWS_PREVIEW_LIMIT ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {`Showing the first ${SKIPPED_ROWS_PREVIEW_LIMIT} of ${uploadResult.rows.length} skipped rows. Download the file to see all of them.`}
                  </Typography>
                ) : null}
                <Box sx={{ mt: 1 }}>
                  <ButtonTwo
                    onClick={handleDownloadSkippedRows}
                    startIcon={<DownloadIcon />}
                  >
                    Download all skipped rows
                  </ButtonTwo>
                </Box>
              </Box>
            )}

            {/* Upload Success Alert */}
            {uploadSuccess && (
              <Alert severity="success" sx={{ width: "100%" }}>
                <Typography variant="body1" fontWeight="bold">
                  Audience added successfully
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <ButtonTwo onClick={handleClose} disabled={loading}>
            {uploadResult ? "Close" : "Cancel"}
          </ButtonTwo>
          {!largeFileTimeout && (
            <ButtonOne
              onClick={handleUpload}
              variant="contained"
              color="primary"
              disabled={!file || loading}
            >
              {loading ? <CircularProgress size={24} /> : "Upload"}
            </ButtonOne>
          )}
        </DialogActions>
      </Dialog>

      <AddAudienceModal
        tagOptions={allTagOptions}
        open={addAudienceModalOpen}
        setCreateTagModalOpen={setCreateTagModalOpen}
        handleClose={() => {
          // Reset to page 0 so the newly added audience (most recently
          // updated, sorted first by the backend) is visible immediately.
          setController((prev) => ({
            ...prev,
            page: 0,
            serverSidePage: 0,
          }));
          fetchData();
          setAddAudienceModalOpen(false);
        }}
      />

      <EditAudienceModal
        tagOptions={allTagOptions}
        audience={selectedEditAudience}
        open={editAudienceModalOpen}
        setCreateTagModalOpen={setCreateTagModalOpen}
        handleClose={() => {
          fetchData();
          setEditAudienceModalOpen(false);
        }}
      />

      <CreateTagModal
        open={createTagModalOpen}
        handleClose={() => {
          // fetchData();
          setCreateTagModalOpen(false);
        }}
      />

      <ManageTagsModal
        tagOptions={allTagOptions}
        open={manageTagsModalOpen}
        onRefresh={() => fetchData()}
        handleClose={() => {
          fetchData();
          setManageTagsModalOpen(false);
        }}
      />

      <BulkUpdateModal
        tagOptions={allTagOptions}
        open={bulkUpdateModalOpen}
        bulkUpdateData={bulkUpdateData}
        setCreateTagModalOpen={setCreateTagModalOpen}
        hasSelectedAll={hasSelectedAll}
        activeFilters={activeFilters}
        handleClose={() => {
          fetchData();
          setBulkUpdateModalOpen(false);
          setSelectedRecords([]);
          setBulkUpdateData([]);
        }}
      />
      <Dialog
        open={Boolean(exportError)}
        onClose={() => setExportError(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "12px",
            border: "1px solid #DCDFE5",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography
            sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#1D2330" }}
          >
            Export In Progress
          </Typography>
          <IconButton size="small" onClick={() => setExportError(null)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Alert
            severity="info"
            sx={{
              borderRadius: "8px",
              border: "1px solid #DCDFE5",
              backgroundColor: "#F0F7FF",
              "& .MuiAlert-icon": {
                color: ColorsConstants.primaryBackgroundColor,
              },
            }}
          >
            <Typography sx={{ ...typography.bodySmall, color: "#1D2330" }}>
              {exportError}
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <ButtonTwo onClick={() => setExportError(null)}>Close</ButtonTwo>
        </DialogActions>
      </Dialog>
    </div>
  );
}
